/** `/login`: request a magic link by email, per `docs/AUTH.md`. */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import { Button, Card, Input } from '../components/ui';
import { t } from '../i18n/fr';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_COOLDOWN_SECONDS = 30;

/**
 * Render the login screen: an email form, then a "check your email" state
 * with a cooldown-gated resend action.
 *
 * @returns The page element.
 */
export function LoginPage() {
  const { requestMagicLink } = useAuth();
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [cooldown, setCooldown] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  function startCooldown() {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    intervalRef.current = setInterval(() => {
      setCooldown((current) => {
        if (current <= 1 && intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        return Math.max(0, current - 1);
      });
    }, 1000);
  }

  async function submit(targetEmail: string) {
    setSubmitting(true);
    setError(undefined);
    try {
      await requestMagicLink(targetEmail);
      setSentTo(targetEmail);
      startCooldown();
    } catch (cause) {
      setError(cause instanceof ApiError ? t('auth.login.genericError') : t('common.error'));
    } finally {
      setSubmitting(false);
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!EMAIL_PATTERN.test(email)) {
      setError(t('auth.login.invalidEmail'));
      return;
    }
    void submit(email);
  }

  if (sentTo) {
    return (
      <div className="auth-screen">
        <Card>
          <h1>{t('auth.checkEmail.title')}</h1>
          <p>{t('auth.checkEmail.body', { email: sentTo })}</p>
          {error ? (
            <p className="ui-field__error" role="alert">
              {error}
            </p>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            disabled={cooldown > 0 || submitting}
            onClick={() => void submit(sentTo)}
          >
            {cooldown > 0
              ? t('auth.checkEmail.resendCooldown', { seconds: cooldown })
              : t('auth.checkEmail.resend')}
          </Button>
          <p>
            <button
              type="button"
              className="ui-btn ui-btn--ghost"
              onClick={() => setSentTo(undefined)}
            >
              {t('auth.checkEmail.changeEmail')}
            </button>
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <Card>
        <h1>{t('auth.login.title')}</h1>
        <p>{t('auth.login.lead')}</p>
        <form onSubmit={onSubmit} noValidate>
          <Input
            label={t('auth.login.emailLabel')}
            type="email"
            name="email"
            placeholder={t('auth.login.emailPlaceholder')}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={error}
            autoComplete="email"
            required
          />
          <Button type="submit" fullWidth disabled={submitting}>
            {submitting ? t('auth.login.submitting') : t('auth.login.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
